/**
 * batch-69 km-balans (FO-515, spec 215) - pure rekenregels van de werk- en
 * privekilometerbalans. I/O-vrij: de API-laag laadt de data in bulk en roept
 * deze functies aan. Alles rekent in kilometers (en liters/kWh), nooit in geld.
 *
 * Onderdelen, in de volgorde van de berekening:
 *   1. kilometerstanden filteren (typefouten eruit, zichtbaar geteld);
 *   2. de maand METEN: de stand op het begin en het einde van de maand (een stand
 *      dichtbij de grens, of lineair tussen twee standen eromheen);
 *   3. anders SCHATTEN: liters (of kWh) maal een verbruiksfactor, met een vaste
 *      voorrang: het gemeten verbruik van het voertuig zelf wint van elke
 *      ingestelde factor; daarna het typegemiddelde en het standaardverbruik;
 *   4. de voertuigkilometers verdelen over de bestuurders naar toewijzingsdagen
 *      (wisseldag naar wie die dag een dagregel had, anders gelijk; de rest van
 *      de afronding naar de langstdurende);
 *   5. de werkkilometers uit de reisritten (woon-werk volgens de instelling);
 *   6. de balans (privé-indicatie = totaal - werk, nooit negatief; werk hoger
 *      dan totaal is een markering) en de vergelijking met de verklaring.
 *
 * De uitkomst is een indicatie. Deze module kent geen oordeel: "boven de
 * drempel" is een vergelijking met een instelling, geen conclusie.
 */

import {
  KM_BALANS_BRON_RANG,
  KM_BALANS_DEFAULTS,
  KM_ENERGIE_DRAGERS,
  KM_FACTOR_HERKOMST_BRON,
  KM_FACTOR_HERKOMST_ZONDER_BANDBREEDTE,
  standaardKmPerEenheid,
  type KmBalansBron,
  type KmBalansInstellingen,
  type KmBalansMarkering,
  type KmBalansZekerheid,
  type KmEnergieDrager,
  type KmFactorHerkomst,
  type VoertuigGebruikType,
} from '@streep/types'
import { amsterdamAllDayStartToUtc, utcToAmsterdamWallclock } from './amsterdam-tijd'
import { dagenInPeriode, periodeGrenzenUtc, periodeUitSleutel, vorigePeriode } from './kostprijs-werkelijk'

const DAG_MS = 86_400_000
/** Onder deze overlap (klokverschil bij een wissel) telt een toewijzing niet op die dag. */
const MIN_OVERLAP_MS = 60_000

const rond1 = (n: number): number => Math.round(n * 10) / 10
const rond3 = (n: number): number => Math.round(n * 1000) / 1000

function ymdPlus(ymd: string, dagen: number): string {
  const d = new Date(Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10))))
  d.setUTCDate(d.getUTCDate() + dagen)
  return d.toISOString().slice(0, 10)
}

/**
 * Is de hoeveelheid van een tankbeurt in kWh (laden)? Tegenhanger van
 * `isLiterEenheid`: leeg is liters, nooit kWh.
 */
export function isKwhEenheid(eenheid: string | null | undefined): boolean {
  if (eenheid == null) return false
  const e = eenheid.trim().toLowerCase().replace(/\s+/g, '')
  return e === 'kwh' || e === 'kw/h' || e === 'kwu'
}

/** Alle NL-kalenderdagen (jjjj-mm-dd) van een periode, oplopend. */
export function dagenVanPeriode(periode: number): string[] {
  const { jaar, maand } = periodeUitSleutel(periode)
  const eerste = `${jaar}-${String(maand).padStart(2, '0')}-01`
  return Array.from({ length: dagenInPeriode(periode) }, (_, i) => ymdPlus(eerste, i))
}

// ─── 1. Kilometerstanden ─────────────────────────────────────────────────────

export interface KmStandInput {
  op: Date
  kmStand: number
  /** Herkomst (TANKBON / TANKPAS / PORTAL / BACKOFFICE / ...), voor de drill-down. */
  bron: string
}

export interface KmStandFilterUitkomst {
  /** De gebruikte standen, oplopend in de tijd en niet-dalend. */
  geldig: KmStandInput[]
  /** Standen die niet in de reeks passen (typefout), nooit stil weggelaten. */
  genegeerd: KmStandInput[]
}

/**
 * Filter implausibele standen. Een stand van 0 of lager, of boven het maximum,
 * valt af. Daarna blijft de langste reeks in de tijd over die NIET DAALT en
 * nergens sneller oploopt dan `maxKmPerDag` (standen binnen een dag tellen als
 * een dag): een kilometerteller loopt niet terug en een auto rijdt geen tien-
 * duizenden kilometers in een paar weken. Een stand die daar niet in past is een
 * typefout (een cijfer te weinig, of juist te veel) en wordt genegeerd. Bij twee
 * even lange reeksen: binnen de reeks de kleinste sprong (de stap die het best
 * bij de volgende stand past), en aan het eind de nieuwste stand: een later
 * bevestigde lagere stand corrigeert een typefout.
 */
export function filterPlausibeleKmStanden(
  standen: readonly KmStandInput[],
  opts: { max?: number; maxKmPerDag?: number } = {},
): KmStandFilterUitkomst {
  const max = opts.max ?? 2_000_000
  const maxPerDag = opts.maxKmPerDag ?? KM_BALANS_DEFAULTS.meetMaxKmPerDag
  const kanVolgen = (eerder: KmStandInput, later: KmStandInput): boolean => {
    const sprong = later.kmStand - eerder.kmStand
    if (sprong < 0) return false
    const dagen = Math.max(1, (later.op.getTime() - eerder.op.getTime()) / DAG_MS)
    return sprong <= maxPerDag * dagen
  }
  const genegeerd: KmStandInput[] = []
  const kandidaten: KmStandInput[] = []
  for (const s of standen) {
    if (!Number.isFinite(s.kmStand) || s.kmStand <= 0 || s.kmStand > max || Number.isNaN(s.op.getTime())) genegeerd.push(s)
    else kandidaten.push(s)
  }
  kandidaten.sort((a, b) => a.op.getTime() - b.op.getTime() || a.kmStand - b.kmStand)
  const n = kandidaten.length
  if (n === 0) return { geldig: [], genegeerd }
  // Langste niet-dalende deelreeks (O(n^2); n is per voertuig en venster klein).
  // Bij gelijke lengte de voorganger met de hoogste stand (de kleinste sprong:
  // een kilometerteller loopt tussen twee standen geen tienduizenden km op),
  // en bij een gelijke stand de nieuwste.
  const lengte = new Array<number>(n).fill(1)
  const vorige = new Array<number>(n).fill(-1)
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < i; j++) {
      if (!kanVolgen(kandidaten[j]!, kandidaten[i]!)) continue
      const kandidaatLengte = lengte[j]! + 1
      const huidig = vorige[i]!
      const beter =
        kandidaatLengte > lengte[i]! ||
        (kandidaatLengte === lengte[i]! &&
          huidig !== -1 &&
          (kandidaten[j]!.kmStand > kandidaten[huidig]!.kmStand ||
            (kandidaten[j]!.kmStand === kandidaten[huidig]!.kmStand && j > huidig)))
      if (beter) {
        lengte[i] = kandidaatLengte
        vorige[i] = j
      }
    }
  }
  let eind = 0
  for (let i = 1; i < n; i++) if (lengte[i]! >= lengte[eind]!) eind = i
  const inReeks = new Set<number>()
  for (let i = eind; i !== -1; i = vorige[i]!) inReeks.add(i)
  const geldig: KmStandInput[] = []
  kandidaten.forEach((s, i) => (inReeks.has(i) ? geldig.push(s) : genegeerd.push(s)))
  genegeerd.sort((a, b) => a.op.getTime() - b.op.getTime())
  return { geldig, genegeerd }
}

// ─── 2. De maand meten ───────────────────────────────────────────────────────

export interface KmAnker {
  /** De (berekende) stand op de grens. */
  km: number
  /**
   * INTERPOLATIE = lineair tussen een stand voor en na de grens; NABIJ = een stand
   * op de grens (afstand 0) of vlak ernaast, doorgetrokken met het dagtempo.
   */
  methode: 'INTERPOLATIE' | 'NABIJ'
  /** Afstand in dagen van de grens tot de dichtstbijzijnde echte stand. */
  afstandDagen: number
  standen: KmStandInput[]
}

export interface KmMeetOpties {
  nabijDagen?: number
  maxIntervalDagen?: number
  hoogDagen?: number
}

/**
 * De kilometerstand op een moment uit een geldige (oplopende, niet-dalende)
 * reeks: exact, lineair tussen de stand ervoor en erna (als die hooguit
 * `maxIntervalDagen` uit elkaar liggen), of anders de dichtstbijzijnde stand
 * binnen `nabijDagen`, doorgetrokken met `tempoKmPerDag` (de kilometers tussen
 * die stand en de grens tellen dan mee). Geen van drieën: onbekend (null).
 */
export function kmStandOpMoment(
  geldig: readonly KmStandInput[],
  moment: Date,
  opts: KmMeetOpties & { tempoKmPerDag?: number | null } = {},
): KmAnker | null {
  const nabij = opts.nabijDagen ?? KM_BALANS_DEFAULTS.meetNabijDagen
  const maxInterval = opts.maxIntervalDagen ?? KM_BALANS_DEFAULTS.meetMaxIntervalDagen
  const t = moment.getTime()
  let voor: KmStandInput | null = null
  let na: KmStandInput | null = null
  for (const s of geldig) {
    const st = s.op.getTime()
    if (st <= t) voor = s
    if (st >= t && na === null) na = s
  }
  if (voor && voor.op.getTime() === t) return { km: voor.kmStand, methode: 'NABIJ', afstandDagen: 0, standen: [voor] }
  if (na && na.op.getTime() === t) return { km: na.kmStand, methode: 'NABIJ', afstandDagen: 0, standen: [na] }
  if (voor && na) {
    const span = na.op.getTime() - voor.op.getTime()
    if (span > 0 && span <= maxInterval * DAG_MS) {
      const fractie = (t - voor.op.getTime()) / span
      const afstand = Math.min(t - voor.op.getTime(), na.op.getTime() - t) / DAG_MS
      return {
        km: rond1(voor.kmStand + (na.kmStand - voor.kmStand) * fractie),
        methode: 'INTERPOLATIE',
        afstandDagen: rond1(afstand),
        standen: [voor, na],
      }
    }
  }
  const dichtbij = [voor, na]
    .filter((s): s is KmStandInput => s !== null)
    .map((s) => ({ s, afstand: Math.abs(s.op.getTime() - t) / DAG_MS }))
    .filter((x) => x.afstand <= nabij)
    .sort((a, b) => a.afstand - b.afstand)[0]
  if (!dichtbij) return null
  // Doortrekken: een stand na de grens ligt al verder (aftrekken), een stand ervoor nog niet (optellen).
  // Het tempo komt bij voorkeur uit het interval naast die stand (aan de kant van de maand: het lokale
  // rijpatroon), anders uit het gemiddelde van de reeks.
  const i = geldig.indexOf(dichtbij.s)
  const buur = dichtbij.s.op.getTime() >= t ? geldig[i + 1] : geldig[i - 1]
  const lokaal = buur ? kmTempoPerDag(buur.op.getTime() > dichtbij.s.op.getTime() ? [dichtbij.s, buur] : [buur, dichtbij.s]) : null
  const tempo = lokaal ?? (opts.tempoKmPerDag != null && opts.tempoKmPerDag > 0 ? opts.tempoKmPerDag : 0)
  const verschuiving = (tempo * (t - dichtbij.s.op.getTime())) / DAG_MS
  return {
    km: rond1(Math.max(0, dichtbij.s.kmStand + verschuiving)),
    methode: 'NABIJ',
    afstandDagen: rond1(dichtbij.afstand),
    standen: [dichtbij.s],
  }
}

/**
 * Het gemiddelde dagtempo (km per dag) van een geldige reeks: van de eerste tot de
 * laatste stand, als die minstens een dag uit elkaar liggen; anders onbekend.
 */
export function kmTempoPerDag(geldig: readonly KmStandInput[]): number | null {
  if (geldig.length < 2) return null
  const eerste = geldig[0]!
  const laatste = geldig[geldig.length - 1]!
  const dagen = (laatste.op.getTime() - eerste.op.getTime()) / DAG_MS
  if (!(dagen >= 1)) return null
  return Math.max(0, (laatste.kmStand - eerste.kmStand) / dagen)
}

export interface KmMaandMeting {
  /** Gemeten kilometers van de maand, of null als begin of einde onbekend is. */
  km: number | null
  begin: KmAnker | null
  eind: KmAnker | null
  zekerheid: Extract<KmBalansZekerheid, 'HOOG' | 'MIDDEL'> | null
  /** Genegeerde standen rond de maand (binnen de nabij-marge van de grenzen). */
  genegeerd: KmStandInput[]
  /** De lopende maand: gemeten tot en met dit moment (nooit voorbij "nu"); anders null. */
  totEnMet: Date | null
}

/** Een grens die is doorgetrokken (geen stand op de grens, geen stand aan beide kanten). */
const isDoorgetrokken = (a: KmAnker): boolean => a.methode === 'NABIJ' && a.afstandDagen > 0

/**
 * Meet de kilometers van een maand uit de kilometerstanden: de stand op de
 * eerste dag 00:00 en op de eerste dag van de volgende maand 00:00 (NL-tijd).
 * Beide grenzen moeten bekend zijn, uit twee verschillende metingen: één stand
 * meet niets. Een grens zonder stand aan beide kanten mag hooguit `nabijDagen`
 * van een stand liggen en wordt doorgetrokken met het dagtempo; liggen de standen
 * verder van de grens, dan is dit geen meting van de hele maand (null, de maand
 * wordt geschat). In de LOPENDE maand (`nu` voor het einde) is het einde "nu":
 * gemeten tot en met vandaag, nooit doorgetrokken naar kilometers die nog niet
 * gereden zijn, en standen na "nu" tellen niet. Zekerheid HOOG als beide grenzen
 * een echte stand binnen `hoogDagen` hebben en geen van beide is doorgetrokken;
 * een doorgetrokken grens is hooguit MIDDEL.
 */
export function meetMaandKm(
  input: { periode: number; standen: readonly KmStandInput[]; nu?: Date } & KmMeetOpties,
): KmMaandMeting {
  const nabij = input.nabijDagen ?? KM_BALANS_DEFAULTS.meetNabijDagen
  const hoog = input.hoogDagen ?? KM_BALANS_DEFAULTS.meetHoogDagen
  const { van, tot } = periodeGrenzenUtc(input.periode)
  const lopend = input.nu != null && input.nu.getTime() < tot.getTime()
  const totEnMet = lopend ? input.nu! : null
  if (input.nu != null && input.nu.getTime() <= van.getTime()) {
    return { km: null, begin: null, eind: null, zekerheid: null, genegeerd: [], totEnMet }
  }
  const standen = totEnMet ? input.standen.filter((s) => s.op.getTime() <= totEnMet.getTime()) : input.standen
  const { geldig, genegeerd } = filterPlausibeleKmStanden(standen)
  const venster = genegeerd.filter(
    (s) => s.op.getTime() >= van.getTime() - nabij * DAG_MS && s.op.getTime() < tot.getTime() + nabij * DAG_MS,
  )
  const tempo = kmTempoPerDag(geldig)
  const begin = kmStandOpMoment(geldig, van, { ...input, tempoKmPerDag: tempo })
  const eind = kmStandOpMoment(geldig, totEnMet ?? tot, { ...input, tempoKmPerDag: tempo })
  if (!begin || !eind) return { km: null, begin, eind, zekerheid: null, genegeerd: venster, totEnMet }
  const zelfdeStand = begin.standen.length === 1 && eind.standen.length === 1 && begin.standen[0] === eind.standen[0]
  if (zelfdeStand) return { km: null, begin, eind, zekerheid: null, genegeerd: venster, totEnMet }
  const hoogZeker = !isDoorgetrokken(begin) && !isDoorgetrokken(eind) && begin.afstandDagen <= hoog && eind.afstandDagen <= hoog
  return {
    km: Math.max(0, rond1(eind.km - begin.km)),
    begin,
    eind,
    zekerheid: hoogZeker ? 'HOOG' : 'MIDDEL',
    genegeerd: venster,
    totEnMet,
  }
}

const dagenTekst = (n: number): string => (n === 1 ? '1 dag' : `${String(n).replace('.', ',')} dagen`)

/**
 * Hoe een gemeten maand tot stand kwam, in gewone taal (scherm, export, portal):
 * standen op de grenzen, lineair tussen standen rond een grens, of doorgetrokken
 * vanaf een stand vlak naast de grens. Null zonder twee grenzen.
 */
export function meetMethodeTekst(
  begin: Pick<KmAnker, 'methode' | 'afstandDagen'> | null,
  eind: Pick<KmAnker, 'methode' | 'afstandDagen'> | null,
): string | null {
  if (!begin || !eind) return null
  const ankers = [begin, eind]
  const lineair = ankers.some((a) => a.methode === 'INTERPOLATIE')
  const doorgetrokken = ankers.filter((a) => a.methode === 'NABIJ' && a.afstandDagen > 0)
  if (!lineair && doorgetrokken.length === 0) return 'kilometerstanden op de maandgrenzen'
  const delen: string[] = []
  if (lineair) delen.push('lineair tussen kilometerstanden rond de maandgrens')
  if (doorgetrokken.length > 0) {
    const verst = Math.max(...doorgetrokken.map((a) => a.afstandDagen))
    delen.push(`doorgetrokken vanaf een kilometerstand op ${dagenTekst(verst)} van de grens`)
  }
  return delen.join('; ')
}

// ─── 3. Schatten: verbruiksfactor ────────────────────────────────────────────

export interface GemetenVerbruik {
  /** Kilometers per liter (1 op X). */
  kmPerLiter: number
  maanden: number
  km: number
  voertuigen: number
}

/**
 * Het gemeten verbruik over een reeks voertuig-maanden (`verbruikPer100Km` uit
 * de tank-tot-tankmeting): het totaal van de kilometers gedeeld door het totaal
 * van de liters, dus gewogen naar de gereden afstand. Een maand met een
 * implausibel verbruik telt niet; te weinig kilometers of voertuigen = null.
 */
export function gemetenKmPerLiter(
  rijen: ReadonlyArray<{ voertuigId: string; kmVerreden: number | null; verbruikPer100Km: number | null }>,
  opts: { minKm: number; minVoertuigen?: number; minVerbruik?: number; maxVerbruik?: number },
): GemetenVerbruik | null {
  const minV = opts.minVerbruik ?? KM_BALANS_DEFAULTS.verbruikPlausibelMin
  const maxV = opts.maxVerbruik ?? KM_BALANS_DEFAULTS.verbruikPlausibelMax
  let km = 0
  let liters = 0
  let maanden = 0
  const voertuigen = new Set<string>()
  for (const r of rijen) {
    if (r.kmVerreden == null || r.verbruikPer100Km == null) continue
    if (!(r.kmVerreden > 0) || r.verbruikPer100Km < minV || r.verbruikPer100Km > maxV) continue
    km += r.kmVerreden
    liters += (r.kmVerreden * r.verbruikPer100Km) / 100
    maanden++
    voertuigen.add(r.voertuigId)
  }
  if (maanden === 0 || liters <= 0 || km < opts.minKm || voertuigen.size < (opts.minVoertuigen ?? 1)) return null
  return { kmPerLiter: rond3(km / liters), maanden, km: rond1(km), voertuigen: voertuigen.size }
}

export interface FactorKandidaten {
  /** Gemeten verbruik van dit voertuig (km per liter); alleen voor liters. */
  voertuigGemeten?: number | null
  voertuigIngesteld?: number | null
  /** Gemeten gemiddelde van het type (km per liter); alleen voor liters. */
  typeGemeten?: number | null
  typeIngesteld?: number | null
  standaardIngesteld?: number | null
}

export interface FactorKeuze {
  kmPerEenheid: number
  herkomst: KmFactorHerkomst
}

/**
 * Kies de verbruiksfactor voor een schatting. Voorrang (spec 215 §A1, ADDENDA):
 *   1. het GEMETEN verbruik van het voertuig zelf (liters);
 *   2. de factor die voor dit voertuig is ingesteld;
 *   3. het gemeten gemiddelde van het voertuigtype (liters);
 *   4. de factor die voor het type is ingesteld;
 *   5. het ingestelde standaardverbruik van de brandstofsoort;
 *   6. het ingebouwde standaardverbruik (1 op 13, of 1 kWh op 5 km).
 * Een instelbare factor wordt dus alleen gebruikt als er geen gemeten verbruik
 * van het voertuig is. Voor kWh bestaat geen gemeten verbruik.
 */
export function kiesVerbruiksfactor(drager: KmEnergieDrager, k: FactorKandidaten): FactorKeuze {
  const geldig = (v: number | null | undefined): v is number => v != null && Number.isFinite(v) && v > 0
  if (drager === 'LITER' && geldig(k.voertuigGemeten)) return { kmPerEenheid: k.voertuigGemeten, herkomst: 'VOERTUIG_GEMETEN' }
  if (geldig(k.voertuigIngesteld)) return { kmPerEenheid: k.voertuigIngesteld, herkomst: 'VOERTUIG_INGESTELD' }
  if (drager === 'LITER' && geldig(k.typeGemeten)) return { kmPerEenheid: k.typeGemeten, herkomst: 'TYPE_GEMETEN' }
  if (geldig(k.typeIngesteld)) return { kmPerEenheid: k.typeIngesteld, herkomst: 'TYPE_INGESTELD' }
  if (geldig(k.standaardIngesteld)) return { kmPerEenheid: k.standaardIngesteld, herkomst: 'STANDAARD_INGESTELD' }
  return { kmPerEenheid: standaardKmPerEenheid(drager), herkomst: 'STANDAARD' }
}

/** Liters (of kWh) naar kilometers: hoeveelheid maal kilometers per eenheid. */
export function schatTotaalKm(hoeveelheid: number, kmPerEenheid: number): number {
  if (!(hoeveelheid > 0) || !(kmPerEenheid > 0)) return 0
  return rond1(hoeveelheid * kmPerEenheid)
}

/** De zwakste (minst betrouwbare) bron. Lege lijst = GEEN. */
export function zwaksteBron(bronnen: readonly KmBalansBron[]): KmBalansBron {
  let zwakst: KmBalansBron | null = null
  for (const b of bronnen) if (zwakst === null || KM_BALANS_BRON_RANG[b] > KM_BALANS_BRON_RANG[zwakst]) zwakst = b
  return zwakst ?? 'GEEN'
}

const ZEKERHEID_RANG: Record<KmBalansZekerheid, number> = { HOOG: 0, MIDDEL: 1, LAAG: 2 }
const ZEKERHEID_VAN_RANG: KmBalansZekerheid[] = ['HOOG', 'MIDDEL', 'LAAG']

export function zwaksteZekerheid(zekerheden: readonly KmBalansZekerheid[]): KmBalansZekerheid | null {
  let zwakst: KmBalansZekerheid | null = null
  for (const z of zekerheden) if (zwakst === null || ZEKERHEID_RANG[z] > ZEKERHEID_RANG[zwakst]) zwakst = z
  return zwakst
}

/** Verlaag een zekerheid met een aantal stappen (nooit lager dan LAAG). */
export function verlaagZekerheid(z: KmBalansZekerheid, stappen = 1): KmBalansZekerheid {
  return ZEKERHEID_VAN_RANG[Math.min(2, ZEKERHEID_RANG[z] + Math.max(0, stappen))]!
}

export interface VoertuigFactorGebruik {
  drager: KmEnergieDrager
  hoeveelheid: number
  kmPerEenheid: number
  herkomst: KmFactorHerkomst
  km: number
}

export interface VoertuigMaandKm {
  bron: KmBalansBron
  km: number | null
  zekerheid: KmBalansZekerheid | null
  /** Plus of min dit percentage bij een schatting met het standaardverbruik. */
  bandbreedtePct: number | null
  meting: KmMaandMeting
  /** Alleen bij een schatting: per drager de hoeveelheid, de factor en de kilometers. */
  factoren: VoertuigFactorGebruik[]
}

/**
 * De kilometers van een voertuig in een maand, met bron en zekerheid. Een
 * METING wint altijd: zijn de standen op beide maandgrenzen bekend, dan wordt
 * er niet geschat (ook niet als er getankt is). Anders: per drager de
 * hoeveelheid maal de gekozen factor (privé-tankbeurten tellen mee: de auto
 * rijdt ze). Geen meting en niets getankt: GEEN (totaal onbekend).
 */
export function bepaalVoertuigMaandKm(input: {
  periode: number
  standen: readonly KmStandInput[]
  hoeveelheden: Readonly<Record<KmEnergieDrager, number>>
  factoren: Partial<Readonly<Record<KmEnergieDrager, FactorKeuze>>>
  bandbreedteStandaardPct: number
  meet?: KmMeetOpties
  /** De lopende maand: meten tot en met nu (zie `meetMaandKm`). */
  nu?: Date
}): VoertuigMaandKm {
  const meting = meetMaandKm({ periode: input.periode, standen: input.standen, nu: input.nu, ...input.meet })
  if (meting.km != null) {
    return { bron: 'GEMETEN', km: meting.km, zekerheid: meting.zekerheid, bandbreedtePct: null, meting, factoren: [] }
  }
  const factoren: VoertuigFactorGebruik[] = []
  for (const drager of KM_ENERGIE_DRAGERS) {
    const hoeveelheid = input.hoeveelheden[drager]
    if (!(hoeveelheid > 0)) continue
    const keuze = input.factoren[drager] ?? { kmPerEenheid: standaardKmPerEenheid(drager), herkomst: 'STANDAARD' as const }
    factoren.push({
      drager,
      hoeveelheid: rond3(hoeveelheid),
      kmPerEenheid: keuze.kmPerEenheid,
      herkomst: keuze.herkomst,
      km: schatTotaalKm(hoeveelheid, keuze.kmPerEenheid),
    })
  }
  if (factoren.length === 0) return { bron: 'GEEN', km: null, zekerheid: null, bandbreedtePct: null, meting, factoren }
  const bron = zwaksteBron(factoren.map((f) => KM_FACTOR_HERKOMST_BRON[f.herkomst]))
  const km = rond1(factoren.reduce((s, f) => s + f.km, 0))
  // De bandbreedte hangt aan de HERKOMST van elke factor: alleen het eigen gemeten
  // verbruik rekent zonder; bij een mix (plug-in hybride) naar rato van de kilometers.
  const bandPct = input.bandbreedteStandaardPct > 0 ? input.bandbreedteStandaardPct : 0
  const bandKm = factoren
    .filter((f) => !KM_FACTOR_HERKOMST_ZONDER_BANDBREEDTE.includes(f.herkomst))
    .reduce((s, f) => s + (f.km * bandPct) / 100, 0)
  return {
    bron,
    km,
    zekerheid: bron === 'GESCHAT_VOERTUIG' ? 'MIDDEL' : 'LAAG',
    bandbreedtePct: bandKm > 0 && km > 0 ? Math.round((bandKm / km) * 100) : null,
    meting,
    factoren,
  }
}

// ─── 4. Verdeling over bestuurders ───────────────────────────────────────────

export interface BestuurderToewijzing {
  employeeId: string
  startDatum: Date
  eindDatum: Date | null
}

export interface BestuurderDeel {
  employeeId: string
  /** Toewijzingsdagen (een gedeelde wisseldag telt als een deel). */
  dagen: number
  wisseldagen: number
  wisseldagenGelijkVerdeeld: number
}

export interface BestuurderVerdeling {
  perBestuurder: BestuurderDeel[]
  /** Dagen met minstens één bestuurder (de noemer van de verdeling). */
  dagenMetBestuurder: number
  dagenZonderBestuurder: number
  wisseldagen: number
  wisseldagenGelijkVerdeeld: number
}

/**
 * Verdeel de dagen van een voertuig in een maand over zijn bestuurders
 * (spec 215 §B4 en §5.2). Per NL-dag: één bestuurder krijgt de dag; zijn er
 * meer (een wissel), dan gaat de dag naar de bestuurder met een dagregel op die
 * dag. Hebben ze er geen van allen een (of meerdere tegelijk), dan wordt de dag
 * gelijk verdeeld over wie die dag een dagregel had (of anders over allemaal),
 * met een markering. Een vaste berijder de hele maand = 100%.
 */
export function verdeelOverBestuurders(input: {
  periode: number
  toewijzingen: readonly BestuurderToewijzing[]
  /** Per medewerker de dagen (jjjj-mm-dd) met een (niet-afwezige) dagregel. */
  dagregelDagen: ReadonlyMap<string, ReadonlySet<string>>
}): BestuurderVerdeling {
  const per = new Map<string, BestuurderDeel>()
  const deel = (employeeId: string): BestuurderDeel => {
    let d = per.get(employeeId)
    if (!d) {
      d = { employeeId, dagen: 0, wisseldagen: 0, wisseldagenGelijkVerdeeld: 0 }
      per.set(employeeId, d)
    }
    return d
  }
  let dagenMet = 0
  let dagenZonder = 0
  let wissel = 0
  let gelijk = 0
  for (const dag of dagenVanPeriode(input.periode)) {
    const ds = amsterdamAllDayStartToUtc(dag).getTime()
    const de = amsterdamAllDayStartToUtc(ymdPlus(dag, 1)).getTime()
    const opDag = new Set<string>()
    for (const t of input.toewijzingen) {
      const s = Math.max(t.startDatum.getTime(), ds)
      const e = Math.min(t.eindDatum ? t.eindDatum.getTime() : de, de)
      if (e - s > MIN_OVERLAP_MS) opDag.add(t.employeeId)
    }
    if (opDag.size === 0) {
      dagenZonder++
      continue
    }
    dagenMet++
    const bestuurders = [...opDag].sort()
    if (bestuurders.length === 1) {
      deel(bestuurders[0]!).dagen += 1
      continue
    }
    wissel++
    const metDagregel = bestuurders.filter((id) => input.dagregelDagen.get(id)?.has(dag) ?? false)
    for (const id of bestuurders) deel(id).wisseldagen += 1
    if (metDagregel.length === 1) {
      deel(metDagregel[0]!).dagen += 1
      continue
    }
    gelijk++
    const verdeelOver = metDagregel.length >= 2 ? metDagregel : bestuurders
    for (const id of verdeelOver) {
      const d = deel(id)
      d.dagen += 1 / verdeelOver.length
      d.wisseldagenGelijkVerdeeld += 1
    }
  }
  return {
    perBestuurder: [...per.values()]
      .map((d) => ({ ...d, dagen: Math.round(d.dagen * 100) / 100 }))
      .sort((a, b) => b.dagen - a.dagen || a.employeeId.localeCompare(b.employeeId)),
    dagenMetBestuurder: dagenMet,
    dagenZonderBestuurder: dagenZonder,
    wisseldagen: wissel,
    wisseldagenGelijkVerdeeld: gelijk,
  }
}

/**
 * Verdeel kilometers naar rato van gewichten, in tienden van een kilometer. De
 * afrondingsrest gaat in zijn geheel naar de langstdurende (het grootste
 * gewicht; bij gelijk gewicht de eerste). De som is altijd exact het totaal.
 */
export function verdeelKmNaarRato(
  km: number,
  gewichten: ReadonlyArray<{ sleutel: string; gewicht: number }>,
): Map<string, number> {
  const uit = new Map<string, number>()
  const som = gewichten.reduce((s, g) => s + Math.max(0, g.gewicht), 0)
  if (gewichten.length === 0 || som <= 0) {
    for (const g of gewichten) uit.set(g.sleutel, 0)
    return uit
  }
  const tienden = Math.round(km * 10)
  const delen = gewichten.map((g) => Math.floor((tienden * Math.max(0, g.gewicht)) / som))
  const rest = tienden - delen.reduce((s, d) => s + d, 0)
  let langst = 0
  gewichten.forEach((g, i) => {
    if (g.gewicht > gewichten[langst]!.gewicht) langst = i
  })
  delen[langst] = delen[langst]! + rest
  gewichten.forEach((g, i) => uit.set(g.sleutel, delen[i]! / 10))
  return uit
}

export interface VoertuigKmVerdeling {
  /** Per bestuurder het toegerekende deel (null = geen meting). */
  perBestuurder: Map<string, number | null>
  /** Het deel van de dagen zonder bestuurder: niet toegerekend (null = geen meting). */
  nietToegerekend: number | null
}

/**
 * Verdeel de kilometers van een voertuig pro rata over de dagen van de maand
 * (spec 215 §B4): elke bestuurder krijgt het deel van zijn dagen; het deel van
 * de dagen zonder bestuurder blijft zichtbaar niet toegerekend (die kilometers
 * zijn van niemand in deze balans). In tienden van een kilometer; de
 * afrondingsrest gaat naar de bestuurder met de meeste dagen, zodat bestuurders
 * plus niet toegerekend exact het voertuigtotaal is.
 */
export function verdeelVoertuigKm(
  km: number | null,
  verdeling: Pick<BestuurderVerdeling, 'perBestuurder' | 'dagenZonderBestuurder'>,
): VoertuigKmVerdeling {
  const per = new Map<string, number | null>()
  if (km == null) {
    for (const p of verdeling.perBestuurder) per.set(p.employeeId, null)
    return { perBestuurder: per, nietToegerekend: null }
  }
  const tienden = Math.round(km * 10)
  const dagenBestuurders = verdeling.perBestuurder.reduce((s, p) => s + Math.max(0, p.dagen), 0)
  const totaalDagen = dagenBestuurders + Math.max(0, verdeling.dagenZonderBestuurder)
  if (dagenBestuurders <= 0 || totaalDagen <= 0) {
    for (const p of verdeling.perBestuurder) per.set(p.employeeId, 0)
    return { perBestuurder: per, nietToegerekend: tienden / 10 }
  }
  const delen = verdeling.perBestuurder.map((p) => Math.floor((tienden * Math.max(0, p.dagen)) / totaalDagen))
  const zonder = Math.floor((tienden * Math.max(0, verdeling.dagenZonderBestuurder)) / totaalDagen)
  let langst = 0
  verdeling.perBestuurder.forEach((p, i) => {
    if (p.dagen > verdeling.perBestuurder[langst]!.dagen) langst = i
  })
  delen[langst] = delen[langst]! + (tienden - zonder - delen.reduce((s, d) => s + d, 0))
  verdeling.perBestuurder.forEach((p, i) => per.set(p.employeeId, delen[i]! / 10))
  return { perBestuurder: per, nietToegerekend: zonder / 10 }
}

// ─── 5. Werkkilometers ───────────────────────────────────────────────────────

export interface MedewerkerToewijzing {
  voertuigId: string
  /** VAST | TIJDELIJK */
  soort: string
  startDatum: Date
  eindDatum: Date | null
}

/**
 * Welke auto('s) de medewerker per NL-dag van de periode had, met het deel van
 * de dag. Voor kilometers telt de auto die er reed: een TIJDELIJKE (vervangende)
 * auto gaat voor de vaste (die staat dan bijvoorbeeld in de garage). Dagen
 * zonder auto ontbreken in de uitkomst.
 */
export function voertuigenPerDag(
  toewijzingen: readonly MedewerkerToewijzing[],
  periode: number,
): Map<string, Array<{ voertuigId: string; aandeel: number }>> {
  const uit = new Map<string, Array<{ voertuigId: string; aandeel: number }>>()
  for (const dag of dagenVanPeriode(periode)) {
    const ds = amsterdamAllDayStartToUtc(dag).getTime()
    const de = amsterdamAllDayStartToUtc(ymdPlus(dag, 1)).getTime()
    const segmenten = (lijst: readonly MedewerkerToewijzing[]) =>
      lijst
        .map((t) => ({
          voertuigId: t.voertuigId,
          s: Math.max(t.startDatum.getTime(), ds),
          e: Math.min(t.eindDatum ? t.eindDatum.getTime() : de, de),
        }))
        .filter((x) => x.e - x.s > MIN_OVERLAP_MS)
    const tijdelijk = segmenten(toewijzingen.filter((t) => t.soort !== 'VAST'))
    const kand = tijdelijk.length > 0 ? tijdelijk : segmenten(toewijzingen.filter((t) => t.soort === 'VAST'))
    if (kand.length === 0) continue
    const duur = new Map<string, number>()
    for (const k of kand) duur.set(k.voertuigId, (duur.get(k.voertuigId) ?? 0) + (k.e - k.s))
    const som = [...duur.values()].reduce((s, d) => s + d, 0)
    uit.set(
      dag,
      [...duur.entries()].map(([voertuigId, d]) => ({ voertuigId, aandeel: d / som })),
    )
  }
  return uit
}

/** Woon-werkritten (ook de oude HEEN/TERUG-ritten). */
export const WOON_WERK_ROUTETYPES: readonly string[] = ['WOON_WERK', 'WERK_WOON', 'HEEN', 'TERUG']

export interface WerkRit {
  routeType: string
  km: number
  /**
   * Een rit zonder afstand: de reisberekening viel terug op de luchtlijn zonder
   * coördinaten (0 km, het geocode-gat van FO-403). De werkkilometers van die dag
   * zijn dan onvolledig, tenzij een goedgekeurde afwijking de werkelijke kilometers
   * gaf (dan is `geschat` false). Een luchtlijnrit met coördinaten is een bruikbare
   * schatting en telt gewoon.
   */
  geschat?: boolean
}

export interface WerkDag {
  /** jjjj-mm-dd */
  dag: string
  ritten: readonly WerkRit[]
  /** Kilometers van piket-oproepen op die dag (consignatie, uit de oproepen). */
  consignatieKm: number
  /** Dagregels op die dag zonder reisberekening (bijvoorbeeld een onvolledig adres). */
  dagregelsZonderReis: number
}

export interface WerkDagUitkomst {
  km: number
  telt: boolean
  reden: 'GEEN_BEDRIJFSAUTO' | 'VOERTUIG_ZONDER_METING' | null
}

export interface WerkKmUitkomst {
  /** Werkkilometers op dagen met een bedrijfsauto waarvan het totaal bekend is. */
  werkKm: number
  perVoertuig: Map<string, number>
  zonderBedrijfsauto: number
  opVoertuigZonderMeting: number
  woonWerkUitgesloten: number
  consignatieKm: number
  dagregelsZonderReis: number
  /** Geschatte ritten (luchtlijn) op dagen met een bedrijfsauto. */
  rittenGeschat: number
  dagenMetWerk: number
  perDag: Map<string, WerkDagUitkomst>
}

/**
 * Tel de werkkilometers van een maand. Per dag de reisritten (woon-werkritten
 * alleen als de instelling ze als werk telt; standaard wel, zoals bij de
 * bijtelling) plus de kilometers van piket-oproepen. Een dag telt in de
 * vergelijking als de medewerker die dag een bedrijfsauto had waarvan het
 * maandtotaal bekend is; anders staat hij apart (zonder bedrijfsauto, of op een
 * voertuig zonder meting) en telt hij niet mee - nooit stil weggelaten.
 */
export function aggregeerWerkKm(input: {
  dagen: readonly WerkDag[]
  woonWerkTeltAlsWerk: boolean
  voertuigPerDag: ReadonlyMap<string, ReadonlyArray<{ voertuigId: string; aandeel: number }>>
  voertuigenMetKm: ReadonlySet<string>
}): WerkKmUitkomst {
  let werk = 0
  let zonderAuto = 0
  let zonderMeting = 0
  let woonWerkUit = 0
  let consignatie = 0
  let zonderReis = 0
  let geschat = 0
  let dagenMetWerk = 0
  const perVoertuig = new Map<string, number>()
  const perDag = new Map<string, WerkDagUitkomst>()
  for (const d of input.dagen) {
    let dagKm = 0
    let dagGeschat = 0
    for (const r of d.ritten) {
      if (r.geschat) dagGeschat++
      if (!(r.km > 0)) continue
      if (!input.woonWerkTeltAlsWerk && WOON_WERK_ROUTETYPES.includes(r.routeType)) {
        woonWerkUit += r.km
        continue
      }
      dagKm += r.km
    }
    if (d.consignatieKm > 0) {
      dagKm += d.consignatieKm
      consignatie += d.consignatieKm
    }
    const autos = input.voertuigPerDag.get(d.dag) ?? []
    // Een ontbrekende of geschatte reisberekening raakt de vergelijking alleen op een dag met bedrijfsauto.
    if (autos.length > 0) {
      zonderReis += d.dagregelsZonderReis
      geschat += dagGeschat
    }
    if (dagKm <= 0) {
      perDag.set(d.dag, { km: 0, telt: true, reden: null })
      continue
    }
    dagenMetWerk++
    if (autos.length === 0) {
      zonderAuto += dagKm
      perDag.set(d.dag, { km: rond1(dagKm), telt: false, reden: 'GEEN_BEDRIJFSAUTO' })
      continue
    }
    let telt = 0
    for (const a of autos) {
      const deel = dagKm * a.aandeel
      if (input.voertuigenMetKm.has(a.voertuigId)) {
        telt += deel
        perVoertuig.set(a.voertuigId, (perVoertuig.get(a.voertuigId) ?? 0) + deel)
      } else {
        zonderMeting += deel
      }
    }
    werk += telt
    perDag.set(d.dag, {
      km: rond1(dagKm),
      telt: telt > 0,
      reden: telt > 0 ? null : 'VOERTUIG_ZONDER_METING',
    })
  }
  return {
    werkKm: rond1(werk),
    perVoertuig: new Map([...perVoertuig].map(([k, v]) => [k, rond1(v)])),
    zonderBedrijfsauto: rond1(zonderAuto),
    opVoertuigZonderMeting: rond1(zonderMeting),
    woonWerkUitgesloten: rond1(woonWerkUit),
    consignatieKm: rond1(consignatie),
    dagregelsZonderReis: zonderReis,
    rittenGeschat: geschat,
    dagenMetWerk,
    perDag,
  }
}

// ─── 6. Balans en vergelijking ───────────────────────────────────────────────

export interface BalansUitkomst {
  totaalKm: number | null
  werkKm: number
  /** Totaal min werk, nooit negatief; null als het totaal onbekend is. */
  priveKm: number | null
  /** Ondergrens en bovengrens bij een bandbreedte (null zonder bandbreedte). */
  priveKmMin: number | null
  priveKmMax: number | null
  werkPct: number | null
  privePct: number | null
  /** Werk hoger dan het (geschatte) totaal: de schatting of de reiskilometers kloppen niet. */
  werkBovenTotaal: boolean
}

/**
 * De balans: privé-indicatie = totaal min werk, nooit kleiner dan 0. Is het werk
 * hoger dan het totaal, dan is de privé-indicatie 0 en volgt de markering
 * "werk hoger dan geschat totaal". `bandKm` is de absolute onzekerheid van het
 * totaal (plus of min); de privé-indicatie krijgt dan een onder- en bovengrens.
 */
export function berekenBalans(input: { totaalKm: number | null; werkKm: number; bandKm?: number | null }): BalansUitkomst {
  const werk = rond1(Math.max(0, input.werkKm))
  if (input.totaalKm == null) {
    return { totaalKm: null, werkKm: werk, priveKm: null, priveKmMin: null, priveKmMax: null, werkPct: null, privePct: null, werkBovenTotaal: false }
  }
  const totaal = rond1(Math.max(0, input.totaalKm))
  const prive = rond1(Math.max(0, totaal - werk))
  const band = input.bandKm != null && input.bandKm > 0 ? input.bandKm : null
  let werkPct: number | null = null
  let privePct: number | null = null
  if (totaal > 0) {
    werkPct = rond1(Math.min(100, (werk / totaal) * 100))
    privePct = rond1(100 - werkPct)
  } else if (werk > 0) {
    werkPct = 100
    privePct = 0
  }
  return {
    totaalKm: totaal,
    werkKm: werk,
    priveKm: prive,
    priveKmMin: band == null ? null : rond1(Math.max(0, totaal - band - werk)),
    priveKmMax: band == null ? null : rond1(Math.max(0, totaal + band - werk)),
    werkPct,
    privePct,
    werkBovenTotaal: werk - totaal > 0.05,
  }
}

export interface VerklaringVergelijking {
  /** Boven de ingestelde drempel bij de verklaring "uitsluitend zakelijk". */
  bovenDrempel: boolean
  weinigWerkdagen: boolean
  /** De werkkilometers zijn onvolledig: de maand telt niet mee in de vergelijking (en breekt de reeks). */
  werkOnvolledig: boolean
  /** Geen werkdagen op werkbonnen: de werkkilometers zijn onbekend, er is niet vergeleken. */
  geenWerkdagen: boolean
  /** De vergelijkingswaarden: bij een bandbreedte de ondergrens (voorzichtig). */
  vergelijkPriveKm: number | null
  vergelijkPrivePct: number | null
}

/**
 * Vergelijk de balans met de gebruiksverklaring (spec 215 §B6). Alleen bij
 * "uitsluitend zakelijk" kan de uitkomst boven de drempel liggen: privé-
 * indicatie boven het ingestelde percentage OF boven het ingestelde aantal
 * kilometers. Bij een bandbreedte telt de ONDERGRENS van de schatting, zodat
 * een grove schatting niet vanzelf een signaal geeft. Zijn de werkkilometers
 * onvolledig (een dagregel zonder reisberekening of een rit zonder afstand door
 * een adres zonder coördinaten), dan is de privé-indicatie te hoog en telt de
 * maand niet mee. "Ook privé" en geen verklaring: alleen monitoren. Een
 * vergelijking, geen oordeel.
 */
export function vergelijkMetVerklaring(input: {
  verklaring: VoertuigGebruikType | null
  balans: BalansUitkomst
  bandKm?: number | null
  drempelPct: number
  drempelKm: number
  werkdagen: number
  minWerkdagen: number
  werkOnvolledig?: boolean
}): VerklaringVergelijking {
  const weinigWerkdagen = input.werkdagen < input.minWerkdagen
  const werkOnvolledig = input.werkOnvolledig === true
  // Zonder een enkele werkdag op een werkbon is het werk onbekend (niet "nul"): niet vergelijken.
  const geenWerkdagen = input.werkdagen <= 0
  const b = input.balans
  if (b.totaalKm == null || b.priveKm == null) {
    return { bovenDrempel: false, weinigWerkdagen, werkOnvolledig, geenWerkdagen, vergelijkPriveKm: null, vergelijkPrivePct: null }
  }
  const band = input.bandKm != null && input.bandKm > 0 ? input.bandKm : 0
  const totaalLaag = Math.max(0, b.totaalKm - band)
  const priveLaag = rond1(Math.max(0, totaalLaag - b.werkKm))
  const pctLaag = totaalLaag > 0 ? rond1((priveLaag / totaalLaag) * 100) : null
  const bovenDrempel =
    input.verklaring === 'UITSLUITEND_ZAKELIJK' &&
    !b.werkBovenTotaal &&
    !werkOnvolledig &&
    !geenWerkdagen &&
    (priveLaag > input.drempelKm || (pctLaag != null && pctLaag > input.drempelPct))
  return { bovenDrempel, weinigWerkdagen, werkOnvolledig, geenWerkdagen, vergelijkPriveKm: priveLaag, vergelijkPrivePct: pctLaag }
}

/**
 * Structureel boven de drempel: de laatste `maanden` kalendermaanden tot en met
 * `eindPeriode` liggen ALLEMAAL boven de drempel. Een ontbrekende maand of een
 * maand met weinig werkdagen breekt de reeks (geen automatische escalatie op
 * een dunne maand, spec 215 §5.5).
 */
export function isStructureelBovenDrempel(
  reeks: ReadonlyArray<{ periode: number; bovenDrempel: boolean; weinigWerkdagen: boolean }>,
  eindPeriode: number,
  maanden: number,
): boolean {
  if (maanden < 1) return false
  const per = new Map(reeks.map((r) => [r.periode, r]))
  let p = eindPeriode
  for (let i = 0; i < maanden; i++) {
    const r = per.get(p)
    if (!r || !r.bovenDrempel || r.weinigWerkdagen) return false
    p = vorigePeriode(p)
  }
  return true
}

export interface VerklaringKandidaat {
  id: string
  type: VoertuigGebruikType
  verklaardOp: Date
  geldigVanaf: Date | null
  geldigTot: Date | null
  ingetrokkenOp: Date | null
  deletedAt: Date | null
}

/**
 * De verklaring die op het peilmoment gold: niet verwijderd, al ingegaan, nog
 * niet verlopen en (nog) niet ingetrokken. Bij meerdere de nieuwste.
 */
export function kiesGeldendeVerklaring<T extends VerklaringKandidaat>(verklaringen: readonly T[], peil: Date): T | null {
  const t = peil.getTime()
  const geldig = verklaringen.filter(
    (v) =>
      !v.deletedAt &&
      (v.geldigVanaf == null || v.geldigVanaf.getTime() <= t) &&
      (v.geldigTot == null || v.geldigTot.getTime() >= t) &&
      (v.ingetrokkenOp == null || v.ingetrokkenOp.getTime() > t),
  )
  geldig.sort((a, b) => b.verklaardOp.getTime() - a.verklaardOp.getTime() || b.id.localeCompare(a.id))
  return geldig[0] ?? null
}

/**
 * Langer dan `maanden` kalendermaanden geen kilometerstand (NL-kalenderdag)?
 * Geen stand bekend = ja.
 */
export function geenRecenteKmStand(laatste: Date | null, nu: Date, maanden: number): boolean {
  if (!laatste) return true
  const l = utcToAmsterdamWallclock(laatste).ymd
  const n = utcToAmsterdamWallclock(nu).ymd
  const jaar = Number(l.slice(0, 4))
  const maand = Number(l.slice(5, 7)) - 1 + maanden
  const doelJaar = jaar + Math.floor(maand / 12)
  const doelMaand = (maand % 12) + 1
  const laatsteDag = new Date(Date.UTC(doelJaar, doelMaand, 0)).getUTCDate()
  const dag = Math.min(Number(l.slice(8, 10)), laatsteDag)
  const grens = `${doelJaar}-${String(doelMaand).padStart(2, '0')}-${String(dag).padStart(2, '0')}`
  return n > grens
}

// ─── Samenstelling per medewerker-maand ──────────────────────────────────────

export interface MedewerkerVoertuigInput {
  voertuigId: string
  /** Totaal van het voertuig in de maand (null = geen meting). */
  km: number | null
  bron: KmBalansBron
  zekerheid: KmBalansZekerheid | null
  bandbreedtePct: number | null
  /** Het deel van het voertuigtotaal voor deze medewerker (null = geen meting). */
  kmToegerekend: number | null
  wisseldagenGelijkVerdeeld: number
  dagenZonderBestuurder: number
  genegeerdeStanden: number
  /**
   * Had de medewerker de auto de hele maand alleen? Zo niet (gedeeld, of dagen
   * zonder bestuurder), dan is zijn deel een verdeling naar dagen en geen meting
   * van wat hij reed: de zekerheid gaat een stap omlaag. Ontbreekt = ja.
   */
  heleMaand?: boolean
}

export interface MedewerkerMaandUitkomst {
  totaalKm: number | null
  bron: KmBalansBron
  zekerheid: KmBalansZekerheid | null
  /** Absolute onzekerheid van het totaal (plus of min). */
  bandKm: number | null
  bandbreedtePct: number | null
  balans: BalansUitkomst
  vergelijking: VerklaringVergelijking
  markeringen: KmBalansMarkering[]
}

/**
 * De balans van één medewerker in één maand, uit de voertuigen die hij reed
 * (met het toegerekende deel) en zijn werkkilometers. Bron en zekerheid zijn die
 * van het zwakste voertuig; ontbreken er reisberekeningen, dan is de zekerheid
 * een stap lager. Alleen voertuigen met een bekend totaal tellen in het totaal;
 * de werkkilometers op andere dagen staan apart (zie `aggregeerWerkKm`).
 */
export function berekenMedewerkerMaand(input: {
  voertuigen: readonly MedewerkerVoertuigInput[]
  werk: WerkKmUitkomst
  werkdagen: number
  verklaring: VoertuigGebruikType | null
  instellingen: Pick<
    KmBalansInstellingen,
    'woonWerkTeltAlsWerk' | 'drempelPrivePct' | 'drempelPriveKm' | 'minWerkdagen'
  >
}): MedewerkerMaandUitkomst {
  const metKm = input.voertuigen.filter((v) => v.bron !== 'GEEN' && v.km != null && v.kmToegerekend != null)
  const totaal = metKm.length > 0 ? rond1(metKm.reduce((s, v) => s + (v.kmToegerekend ?? 0), 0)) : null
  const bron = metKm.length > 0 ? zwaksteBron(metKm.map((v) => v.bron)) : 'GEEN'
  let zekerheid = metKm.length > 0 ? zwaksteZekerheid(metKm.map((v) => v.zekerheid ?? 'LAAG')) : null
  const werkOnvolledig = input.werk.dagregelsZonderReis > 0 || input.werk.rittenGeschat > 0
  if (zekerheid && werkOnvolledig) zekerheid = verlaagZekerheid(zekerheid)
  // Een deel van een auto (gedeeld, of maar een deel van de maand toegewezen) is een verdeling naar dagen.
  if (zekerheid && metKm.some((v) => v.heleMaand === false)) zekerheid = verlaagZekerheid(zekerheid)
  const bandKmRuw = metKm.reduce((s, v) => s + ((v.kmToegerekend ?? 0) * (v.bandbreedtePct ?? 0)) / 100, 0)
  const bandKm = bandKmRuw > 0 ? rond1(bandKmRuw) : null
  const balans = berekenBalans({ totaalKm: totaal, werkKm: input.werk.werkKm, bandKm })
  const vergelijking = vergelijkMetVerklaring({
    verklaring: input.verklaring,
    balans,
    bandKm,
    drempelPct: input.instellingen.drempelPrivePct,
    drempelKm: input.instellingen.drempelPriveKm,
    werkdagen: input.werkdagen,
    minWerkdagen: input.instellingen.minWerkdagen,
    werkOnvolledig,
  })
  const markeringen: KmBalansMarkering[] = []
  if (balans.werkBovenTotaal) markeringen.push('WERK_BOVEN_TOTAAL')
  if (werkOnvolledig) markeringen.push('WERK_ONVOLLEDIG')
  if (input.voertuigen.some((v) => v.wisseldagenGelijkVerdeeld > 0)) markeringen.push('WISSELDAG_GELIJK_VERDEELD')
  if (input.voertuigen.some((v) => v.dagenZonderBestuurder > 0 && v.bron !== 'GEEN')) markeringen.push('DAGEN_ZONDER_BESTUURDER')
  if (input.voertuigen.some((v) => v.genegeerdeStanden > 0)) markeringen.push('KM_STANDEN_GENEGEERD')
  if (vergelijking.geenWerkdagen) markeringen.push('GEEN_WERKDAGEN')
  else if (vergelijking.weinigWerkdagen) markeringen.push('WEINIG_WERKDAGEN')
  if (input.werk.opVoertuigZonderMeting > 0 || input.voertuigen.some((v) => v.bron === 'GEEN')) {
    markeringen.push('VOERTUIG_ZONDER_METING')
  }
  if (input.werk.zonderBedrijfsauto > 0) markeringen.push('WERK_ZONDER_BEDRIJFSAUTO')
  if (!input.instellingen.woonWerkTeltAlsWerk && input.werk.woonWerkUitgesloten > 0) markeringen.push('WOON_WERK_NIET_ALS_WERK')
  return {
    totaalKm: totaal,
    bron,
    zekerheid,
    bandKm,
    bandbreedtePct: bandKm != null && totaal != null && totaal > 0 ? Math.round((bandKm / totaal) * 100) : null,
    balans,
    vergelijking,
    markeringen,
  }
}
